import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrgSettings, updateOrgSettings } from '../api/tournamentsApi';
import { Button, Spinner, PageError } from '../components/UI';
import { useToast } from '../components/Toast';

// ─── Иконки ───────────────────────────────────────────────────────────────────

const IconArrowLeft = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const IconSettings = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

// ─── Toggle ───────────────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    style={{
      position: 'relative',
      width: 44,
      height: 24,
      borderRadius: 12,
      background: checked ? '#6366f1' : '#d1d5db',
      border: 'none',
      padding: 0,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 0.2s',
      flexShrink: 0,
    }}
  >
    <span style={{
      position: 'absolute',
      top: 2,
      left: checked ? 22 : 2,
      width: 20,
      height: 20,
      borderRadius: '50%',
      background: '#fff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      transition: 'left 0.2s',
      display: 'block',
    }} />
  </button>
);

// ─── Sport Options ────────────────────────────────────────────────────────────

const SPORT_OPTIONS = [
  { value: 'PICKLEBALL', label: 'Пиклбол', available: true },
  { value: 'PADEL',      label: 'Падел',   available: true },
  { value: 'TENNIS',           label: 'Теннис',               available: false },
  { value: 'BADMINTON',        label: 'Бадминтон',            available: false },
  { value: 'BEACH_VOLLEYBALL', label: 'Пляжный волейбол',     available: false },
  { value: 'SQUASH',           label: 'Сквош',                available: false },
  { value: 'TABLE_TENNIS',     label: 'Настольный теннис',    available: false },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

const OrgSettingsPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [tournamentsEnabled, setTournamentsEnabled] = useState(false);
  const [sport, setSport] = useState('');

  // Ошибки валидации
  const [sportError, setSportError] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const data = await getOrgSettings();
      setTournamentsEnabled(data.tournamentsEnabled ?? false);
      setSport(data.sport ?? '');
    } catch (err) {
      if (err.status === 403) {
        setPageError('Только владелец организации может просматривать настройки.');
      } else {
        setPageError(err.message || 'Не удалось загрузить настройки');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    // Валидация: если турниры включены — нужен вид спорта
    if (tournamentsEnabled && !sport) {
      setSportError('Выберите вид спорта');
      return;
    }
    setSportError('');
    setSaving(true);
    try {
      await updateOrgSettings({
        tournamentsEnabled,
        sport: tournamentsEnabled ? sport : null,
      });
      addToast('Настройки сохранены', 'success');
    } catch (err) {
      if (err.status === 403) {
        addToast('Только владелец организации может менять настройки', 'error');
      } else {
        addToast(`Ошибка сохранения: ${err.message}`, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <Spinner text="Загрузка настроек..." />;

  if (pageError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f8fafc' }}>
        <div style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          padding: '14px 16px 16px',
          color: '#fff',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 8, padding: '5px 10px',
                color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              <IconArrowLeft />
            </button>
            <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Настройки организации</h1>
          </div>
        </div>
        <PageError message={pageError} onRetry={fetchSettings} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f8fafc' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        padding: '14px 16px 16px',
        color: '#fff',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 8, padding: '5px 10px',
                color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              <IconArrowLeft />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                background: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IconSettings />
              </div>
              <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
                Настройки организации
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, overflowY: 'auto' }}>

        {/* Секция «Турниры» */}
        <div style={{
          background: '#fff',
          borderRadius: 14,
          border: '1.5px solid #f0f0f5',
          boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}>
          {/* Заголовок секции */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f0f0f5',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: '#9ca3af',
          }}>
            Турниры
          </div>

          {/* Тумблер tournamentsEnabled */}
          <div style={{
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            borderBottom: tournamentsEnabled ? '1px solid #f0f0f5' : 'none',
          }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
                Включить турниры
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                Позволяет создавать турниры для событий организации
              </div>
            </div>
            <Toggle
              checked={tournamentsEnabled}
              onChange={(val) => {
                setTournamentsEnabled(val);
                if (!val) setSportError('');
              }}
            />
          </div>

          {/* Селект вида спорта — показывается только когда турниры включены */}
          {tournamentsEnabled && (
            <div style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                Вид спорта
                <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>
              </div>
              <div style={{ position: 'relative' }}>
                <select
                  value={sport}
                  onChange={e => {
                    setSport(e.target.value);
                    if (e.target.value) setSportError('');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 32px 10px 12px',
                    borderRadius: 10,
                    border: `1.5px solid ${sportError ? '#fca5a5' : '#e5e7eb'}`,
                    fontSize: 15,
                    color: sport ? '#111827' : '#9ca3af',
                    background: sportError ? '#fff7f7' : '#fff',
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    cursor: 'pointer',
                  }}
                  onFocus={e => { e.target.style.borderColor = sportError ? '#ef4444' : '#6366f1'; }}
                  onBlur={e => { e.target.style.borderColor = sportError ? '#fca5a5' : '#e5e7eb'; }}
                >
                  <option value="">Выберите вид спорта</option>
                  {SPORT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value} disabled={!opt.available}>
                      {opt.label}{!opt.available ? ' — скоро' : ''}
                    </option>
                  ))}
                </select>
                <div style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>
              {sportError && (
                <span style={{ fontSize: 12, color: '#dc2626', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  {sportError}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Кнопка сохранения */}
        <Button
          variant="primary"
          fullWidth
          loading={saving}
          onClick={handleSave}
        >
          Сохранить
        </Button>
      </div>
    </div>
  );
};

export default OrgSettingsPage;
